import { Button, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import baseURL from "../../../../configs/api";
import { validateMin } from "../../../../utils/validateMin";
import CustomFormatDatePicker from "../../../../components/common/CustomFormatDatePicker";
import useGeneral from "../../../../components/hooks/useGeneral";
import { useTheme } from "../../../../components/hooks/useTheme";
import PleaseWaitPopup from "../../../PleaseWaitPopup";
import useDelete from "../../../../components/hooks/useDelete";
import colorConfigs from "../../../../configs/colorConfigs";
import useAuth from "../../../../components/hooks/useAuth";
import Cookies from "js-cookie";
import useTenant from "../../../../components/hooks/useTenant";

export const EditSecurityDeposits = () => {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { auth }: any = useAuth();
  const { handleTenantError }: any = useTenant();
  const idNumber = useLocation();
  const navigate = useNavigate();
  const { dateFormat }: any = useGeneral();
  const { themeStyles } = useTheme();
  const { setDeletePopup, setData: setDeleteData }: any = useDelete();
  const [name, setName] = useState<string>("");
  const [reservationType, setReservationType] = useState<string>("");
  const [withBranch, setWithBranch] = useState<string>("No");
  const [branchId, setBranchId] = useState<string>("");
  const [withAdditionalCharge, setWithAdditionalCharge] = useState<string>("No");
  const [additionalChargeId, setAdditionalChargeId] = useState<string>("");
  const [withVehicleClass, setWithVehicleClass] = useState<string>("No");
  const [vehicleClassId, setVehicleClassId] = useState<string>("");
  const [withPickupLocation, setWithPickupLocation] = useState<string>("No");
  const [withReturnLocation, setWithReturnLocation] = useState<string>("No");
  const [pickupLocationId, setPickupLocationId] = useState<string>("");
  const [returnLocationId, setReturnLocationId] = useState<string>("");
  const [cumulative, setCumulative] = useState<string>("No");
  const [includeQuantities, setIncludeQuantities] = useState<string>("No");
  const [depositAmount, setDepositAmount] = useState<string>("");
  const [currency, setCurrency] = useState<string>("");
  const [effectiveFrom, setEffectiveFrom] = useState<string>("");
  const [effectiveUntil, setEffectiveUntil] = useState<string>("");
  const [isActive, setIsActive] = useState<string>("No");
  const [label, setLabel] = useState<string>("");
  const [additionalOptions, setAdditionalOptions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [pickupLocations, setPickupLocations] = useState<string>("");
  const [pickupLocationsOptions, setPickupLocationsOptions] = useState([]);
  const [
    pickupLocationsOptionsOfParticular,
    setPickupLocationsOptionsOfParticular,
  ] = useState([]);
  const [returnLocations, setReturnLocations] = useState<string>("");
  const [returnLocationsOptions, setReturnLocationsOptions] = useState([]);
  const [
    returnLocationsOptionsOfParticular,
    setReturnLocationsOptionsOfParticular,
  ] = useState([]);
  const [vehicleClassesOptions, setVehicleClassesOptions] = useState([]);
  const [vehicleClassesOptionsOfParticular, setVehicleClassesOptionsOfParticular] = useState([]);
  const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(true);

  useEffect(() => {
    axios
      .get(`${baseURL}branch`, {
        headers: {
          'X-Tenant-ID': tenantId,
        }
      })
      .then((response) => {
        setBranches(() =>
          response?.data?.map((item: any) => {
            return { value: item.id, label: item.branchName };
          })
        );
        handleTenantError(null);
      })
      .catch((error) => {
        handleTenantError(error)
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    axios
      .get(`${baseURL}additionalCharge`, {
        headers: {
          'X-Tenant-ID': tenantId,
        }
      })
      .then((response) => {
        setAdditionalOptions(() =>
          response.data.map((item: any) => {
            return { value: item.id, label: item.name };
          })
        );
        handleTenantError(null);
      })
      .catch((error) => {
        handleTenantError(error);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    axios
      .get(`${baseURL}vehicleClass`, {
        headers: {
          'X-Tenant-ID': tenantId,
        }
      })
      .then((response) => {
        const formattedVehicleClasses = response.data.map((item: any) => ({
          value: item.id,
          label: item.name,
          branchId: item.branchId,
        }));

        setVehicleClassesOptions(formattedVehicleClasses);
        handleTenantError(null);
      })
      .catch((error) => {
        handleTenantError(error);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (branchId && vehicleClassesOptions)
      setVehicleClassesOptionsOfParticular(() =>
        vehicleClassesOptions.filter((item: any) => item.branchId === parseInt(branchId))
      );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, vehicleClassesOptions]);

  useEffect(() => {
    axios
      .get(`${baseURL}location`, {
        headers: {
          'X-Tenant-ID': tenantId,
        }
      })
      .then((response) => {
        const formattedLocations = response.data.map((item: any) => ({
          value: item.id,
          label: item.locationName,
          branchId: item.branchId,
        }));

        setPickupLocationsOptions(formattedLocations);
        setReturnLocationsOptions(formattedLocations);

        if (branchId) {
          const filteredPickupLocations = formattedLocations.filter(
            (item: any) => item.branchId === parseInt(branchId)
          );
          setPickupLocationsOptionsOfParticular(filteredPickupLocations);

          const filteredReturnLocations = formattedLocations.filter(
            (item: any) => item.branchId === parseInt(branchId)
          );
          setReturnLocationsOptionsOfParticular(filteredReturnLocations);

          const defaultPickup = filteredPickupLocations.find(
            (item: any) => item.value === pickupLocationId
          );
          const defaultReturn = filteredReturnLocations.find(
            (item: any) => item.value === returnLocationId
          );

          if (defaultPickup) {
            setPickupLocations(defaultPickup.value);
          } else if (filteredPickupLocations.length > 0) {
            setPickupLocations(filteredPickupLocations[0].value);
          }

          if (defaultReturn) {
            setReturnLocations(defaultReturn.value);
          } else if (filteredReturnLocations.length > 0) {
            setReturnLocations(filteredReturnLocations[0].value);
          }
        }
        handleTenantError(null);
      })
      .catch((error) => {
        handleTenantError(error);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    setPleaseWaitPopup(true);
    axios
      .get(`${baseURL}securityDeposit/${idNumber?.state}`, {
        headers: {
          'X-Tenant-ID': tenantId,
        }
      })
      .then((response) => {
        setName(response?.data?.name);
        setReservationType(response?.data?.reservationType);
        setWithBranch(response?.data?.withBranch ? "Yes" : "No");
        setBranchId(response?.data?.branchId);
        setWithAdditionalCharge(response?.data?.withAdditionalCharge ? "Yes" : "No");
        setAdditionalChargeId(response?.data?.additionalChargeId);
        setWithVehicleClass(response?.data?.withVehicleClass ? "Yes" : "No");
        setVehicleClassId(response?.data?.vehicleClassId);
        setWithPickupLocation(response?.data?.withPickupLocation ? "Yes" : "No");
        setPickupLocationId(response?.data?.pickupLocationId);
        setWithReturnLocation(response?.data?.withReturnLocation ? "Yes" : "No");
        setReturnLocationId(response?.data?.returnLocationId)
        setCumulative(response?.data?.cumulative ? "Yes" : "No");
        setIncludeQuantities(response?.data?.includeQuantities ? "Yes" : "No");
        setDepositAmount(response?.data?.depositAmount);
        setCurrency(response?.data?.currency);
        if (response?.data?.effectiveFrom) {
          setEffectiveFrom(response?.data?.effectiveFrom?.split("T")[0]);
        }

        if (response?.data?.effectiveUntil) {
          setEffectiveUntil(response?.data?.effectiveUntil?.split("T")[0]);
        }

        setIsActive(response?.data?.isActive ? "Yes" : "No");
        setLabel(response?.data?.label);
        setPleaseWaitPopup(false);
        handleTenantError(null);
      })
      .catch((error) => {
        setPleaseWaitPopup(false);
        handleTenantError(error);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idNumber]);

  const handlePickupLocationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setPickupLocations(event.target.value);
  };

  const handleReturnLocationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setReturnLocations(event.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    setPleaseWaitPopup(true);
    e.preventDefault();

    await axios
      .put(
        `${baseURL}securityDeposit?userId=${auth?.id}`,
        JSON.stringify({
          id: idNumber?.state,
          name,
          reservationType,
          withBranch: withBranch === "Yes",
          branchId,
          withAdditionalCharge: withAdditionalCharge === "Yes",
          additionalChargeId,
          withVehicleClass: withVehicleClass === "Yes",
          vehicleClassId,
          withPickupLocation: withPickupLocation === "Yes",
          pickupLocationId: pickupLocations,
          withReturnLocation: withReturnLocation === "Yes",
          returnLocationId: returnLocations,
          cumulative: cumulative === "Yes",
          includeQuantities: includeQuantities === "Yes",
          depositAmount,
          currency,
          effectiveFrom,
          effectiveUntil,
          isActive: isActive === "Yes",
          label,
        }),
        {
          headers: {
            'X-Tenant-ID': tenantId,
            "Content-Type": "application/json"
          },
          withCredentials: true,
        }
      )
      .then(() => {
        setPleaseWaitPopup(false);
        navigate(-1);
        handleTenantError(null);
      })
      .catch((error) => {
        setPleaseWaitPopup(false);
        handleTenantError(error);
      });
  };

  const deleteSecurityDeposit = () => {
    setDeleteData({
      id: idNumber,
      tilte: `Delete`,
      summary: `Are you sure you want to delete this Security Deposit?`,
      isDelete: (callback: any) => {
        fetch(`${baseURL}securityDeposit?id=${idNumber?.state}&userId=${auth?.id}`, {
          method: 'DELETE',
          headers: {
            'X-Tenant-ID': tenantId,
          }
        })
          .then(() => {
            setPleaseWaitPopup(false);
            navigate(-1);
            callback();
            handleTenantError(null);
          })
          .catch((error) => {
            setPleaseWaitPopup(false);
            callback();
            handleTenantError(error);
          });
      }
    });

    setDeletePopup(true);
  };

  return (
    <>
      <div className={`px-[24px] py-[20px] min-h-screen h-full ${themeStyles.main.tailwindSectionBg}`}>
        <div className={`w-full ${themeStyles.main.tailwindMainBg} rounded-lg p-[10px] pb-[40px] flex flex-col gap-2.51`}>
          <div className="w-full mt-2">
            <div className={`w-full pb-4`}>
              <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                <div className="flex items-center gap-1.5">
                  <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Edit Security Deposit</h2>
                </div>

                {
                  disabled && (
                    <button className={`px-[8px] py-[6px] bg-transparent shadow-sm rounded-lg border ${themeStyles.main.borderColor} text-white flex items-center gap-1.5`}
                      onClick={() => setDisabled(false)}
                    >
                      <span className={`material-symbols-outlined text-[25px] font-light ${themeStyles.main.textSubHeadingColor}`}>
                        edit
                      </span>
                      <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>Edit</span>
                    </button>
                  )
                }
              </div>

              <div className="mt-2 p-2">
                <form onSubmit={(e) => handleSubmit(e)}>
                  <div className="w-full px-2">
                    <h5 className="mt-1 pb-0 flex justify-start items-center text-gray-500">
                      Rule Overview
                    </h5>
                    <hr className="mb-4" />
                    <div className="grid grid-cols-3 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Name</label>
                          <input
                            type="text"
                            className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={name}
                            disabled={disabled}
                            onChange={(e) => setName(e.target.value)}
                            required
                          />
                        </div>

                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Deposit Amount</label>
                          <input
                            type="number"
                            step={0.01}
                            min={0}
                            className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={depositAmount}
                            disabled={disabled}
                            onChange={(e) => setDepositAmount(validateMin(e.target.value, 0).toString())}
                            required
                          />
                        </div>

                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Currency</label>
                          <input
                            type="text"
                            className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={currency}
                            disabled={disabled}
                            onChange={(e) => setCurrency(e.target.value)}
                            required
                          />
                        </div>

                        <div className="mt-4">
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>With Branch?</label>
                          <RadioGroup
                            row
                            value={withBranch}
                            onChange={(e) => setWithBranch(e.target.value)}
                          >
                            <FormControlLabel
                              value="Yes" control=
                              {<Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="Yes"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                            <FormControlLabel value="No" control={
                              <Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="No"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                          </RadioGroup>
                        </div>

                        <div className="mt-4">
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>With Additional Charge?</label>
                          <RadioGroup
                            row
                            value={withAdditionalCharge}
                            onChange={(e) => setWithAdditionalCharge(e.target.value)}
                          >
                            <FormControlLabel
                              value="Yes" control=
                              {<Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="Yes"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                            <FormControlLabel value="No" control={
                              <Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="No"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                          </RadioGroup>
                        </div>

                        <div className="mt-4">
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>With Vehicle Class?</label>
                          <RadioGroup
                            row
                            value={withVehicleClass}
                            onChange={(e) => setWithVehicleClass(e.target.value)}
                          >
                            <FormControlLabel
                              value="Yes" control=
                              {<Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="Yes"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                            <FormControlLabel value="No" control={
                              <Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="No"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                          </RadioGroup>
                        </div>

                        <div className="mt-4">
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>With Pickup Location?</label>
                          <RadioGroup
                            row
                            value={withPickupLocation}
                            onChange={(e) => setWithPickupLocation(e.target.value)}
                          >
                            <FormControlLabel
                              value="Yes" control=
                              {<Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="Yes"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                            <FormControlLabel value="No" control={
                              <Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="No"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                          </RadioGroup>
                        </div>

                        <div className="mt-4">
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>With Return Location?</label>
                          <RadioGroup
                            row
                            value={withReturnLocation}
                            onChange={(e) => setWithReturnLocation(e.target.value)}
                          >
                            <FormControlLabel
                              value="Yes" control=
                              {<Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="Yes"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                            <FormControlLabel value="No" control={
                              <Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="No"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                          </RadioGroup>
                        </div>

                        <div className="mt-4">
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>With Cumulative?</label>
                          <RadioGroup
                            row
                            value={cumulative}
                            onChange={(e) => setCumulative(e.target.value)}
                          >
                            <FormControlLabel
                              value="Yes" control=
                              {<Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="Yes"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                            <FormControlLabel value="No" control={
                              <Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="No"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                          </RadioGroup>
                        </div>

                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Effective From</label>
                          <CustomFormatDatePicker
                            value={effectiveFrom}
                            disabled={disabled}
                            onChange={setEffectiveFrom}
                            dateFormat={dateFormat}
                          />
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Reservation Type</label>
                          <select
                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={reservationType}
                            disabled={disabled}
                            onChange={(e) => setReservationType(e.target.value)}
                          >
                            <option value="Reservations">Reservations</option>
                          </select>
                        </div>

                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Branch</label>
                          <select
                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={branchId}
                            disabled={disabled}
                            onChange={(e) => setBranchId(e.target.value)}
                          >
                            {branches?.map((role: any, index: number) => (
                              <option key={index} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Additional Charge</label>
                          <select
                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={additionalChargeId}
                            disabled={disabled}
                            onChange={(e) => setAdditionalChargeId(e.target.value)}
                          >
                            {additionalOptions.map((item: any) => {
                              return <option value={item.value}>{item.label}</option>;
                            })}
                          </select>
                        </div>

                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Vehicle Classes</label>
                          <select
                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={vehicleClassId}
                            disabled={disabled}
                            onChange={(e) => setVehicleClassId(e.target.value)}
                          >
                            {vehicleClassesOptionsOfParticular.map((item: any) => (
                              <option key={item.value} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Pickup Location</label>
                          <select
                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={pickupLocations}
                            disabled={disabled}
                            onChange={handlePickupLocationChange}
                          >
                            {(pickupLocationsOptionsOfParticular.length > 0 ? pickupLocationsOptionsOfParticular : pickupLocationsOptions).map(
                              (location: { value: string; label: string }) => (
                                <option key={location.value} value={location.value}>
                                  {location.label}
                                </option>
                              )
                            )}
                          </select>
                        </div>

                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Return Location</label>
                          <select
                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={returnLocations}
                            disabled={disabled}
                            onChange={handleReturnLocationChange}
                          >
                            {(returnLocationsOptionsOfParticular.length > 0 ? returnLocationsOptionsOfParticular : returnLocationsOptions).map(
                              (location: { value: string; label: string }) => (
                                <option key={location.value} value={location.value}>
                                  {location.label}
                                </option>
                              )
                            )}
                          </select>
                        </div>

                        <div className="mt-4">
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Include Quantities?</label>
                          <RadioGroup
                            row
                            value={includeQuantities}
                            onChange={(e) => setIncludeQuantities(e.target.value)}
                          >
                            <FormControlLabel
                              value="Yes" control=
                              {<Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="Yes"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                            <FormControlLabel value="No" control={
                              <Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="No"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                          </RadioGroup>
                        </div>

                        <div className="mt-4">
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Active?</label>
                          <RadioGroup
                            row
                            value={isActive}
                            onChange={(e) => setIsActive(e.target.value)}
                          >
                            <FormControlLabel
                              value="Yes" control=
                              {<Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="Yes"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                            <FormControlLabel value="No" control={
                              <Radio
                                disabled={disabled}
                                sx={{
                                  color: "gray",
                                  "&.Mui-checked": { color: "#167AC3" },
                                  "&.Mui-disabled": { color: themeStyles.form.labelTextColor },
                                  "&.Mui-disabled.Mui-checked": { color: "#167AC3" },
                                }}
                              />} label="No"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                                "&.Mui-disabled .MuiFormControlLabel-label": {
                                  color: themeStyles.form.labelTextColor,
                                },
                              }}
                            />
                          </RadioGroup>
                        </div>

                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Effective Until</label>
                          <CustomFormatDatePicker
                            value={effectiveUntil}
                            disabled={disabled}
                            onChange={setEffectiveUntil}
                            dateFormat={dateFormat}
                          />
                        </div>
                      </div>
                    </div>

                    <h5 className="mt-6 pb-0 flex justify-start items-center text-gray-500">
                      Website
                    </h5>
                    <hr className="mb-4" />

                    <div className="grid grid-cols-3 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Label</label>
                          <input
                            type="text"
                            className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={label}
                            disabled={disabled}
                            onChange={(e) => setLabel(e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4 mt-6">
                      {
                        !disabled && (
                          <Button variant="contained"
                            sx={{
                              backgroundColor: colorConfigs.btn.main.bg
                            }}
                            type="submit">
                            Save
                          </Button>
                        )
                      }

                      <Button variant="contained"
                        sx={{
                          backgroundColor: colorConfigs.btn.cancel.bg,
                          border: colorConfigs.btn.cancel.border,
                          color: colorConfigs.btn.cancel.color
                        }}
                        onClick={() => navigate(-1)}>
                        Cancel
                      </Button>

                      <Button variant="contained"
                        sx={{
                          backgroundColor: colorConfigs.btn.delete.bg,
                        }}
                        onClick={deleteSecurityDeposit}>
                        Delete
                      </Button>
                    </div>

                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PleaseWaitPopup pleaseWaitPopup={pleaseWaitPopup} />
    </>
  )
}
